// @givmo/org-console — THE TEAM PAGE: one composition for the Admin Console's Team access and for
// every app's own Team page, in both consoles (Org Machinery OM-96).
//
// ══ WHO SEES WHICH VIEW ════════════════════════════════════════════════════════════════════════
//
// Without `module` it is Team access: the whole team, every invite, every app's requests, the AIs
// connected to the organization (D-CP-51's order: the people on the team, the people on their way
// in, the people asking, then the rest).
//
// With `module` it is that app's Team page, and what it holds depends on who is looking:
//   * someone who may manage the whole team (`write_member_access`, a Global Admin by tier) gets
//     Team access's sections IN PLACE — the same roster, the same invite, the same requests — with
//     the app's own column on the roster and the app already switched on in the invite. They stay in
//     the app they were working in, and nothing on the page sends them anywhere else (Jonathan,
//     2026-10-24: "you are still in whatever module you were in, it just gives you the exact
//     functionality and view as the admin console team page");
//   * anyone else (an Owner of the app) gets the app's own view: the people who can open it, the
//     invites that include it, its requests, and the invite that can give only it (D-CPO-24 clause 7).
//
// ⚠ THE VIEW IS DECIDED BY A CAPABILITY ON A SETTLED READ, NEVER BY AN ADMIN FLAG (the build law),
// and the page waits for that read rather than drawing one view and swapping to the other under the
// person. A read that failed draws the app's own view: every operation in it is the server's to
// allow, and nothing in it is wider than the person holds.
//
// DATA CONTRACT: `<prefix>-team-view` wraps the sections, its `data-team-view` naming which view
// drew (`organization`, `in-place` or `app`); `<prefix>-team-pending` is the wait for the standing
// read. The sections keep their own families (`<prefix>-members-*`, `<prefix>-invites-*`,
// `org-access-requests-*`, `<prefix>-connected-ai-*`).

import { testIdProp } from '@givmo/design-system';
import type { ModuleStandings } from './module-verdict';
import { testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { OrgAccessRequestsPanel } from './org-access-requests-panel';
import { OrgConnectedAiPanel } from './org-connected-ai-panel';
import { OrgInvitesPanel } from './org-invites-panel';
import { OrgMembersPanel } from './org-members-panel';
import { CAPABILITY_WRITE_MEMBER_ACCESS, holdsCapability } from './standing-read';
import { useOrgAccessGate } from './use-org-console';

/**
 * The count each module's Team row wears in the sidebar: what that Team page lists (OM-96).
 *
 * Someone whose app Team pages show the whole team (`write_member_access`) sees every app's requests
 * there, so every Team row counts them all, as the Admin Console's Team access row does. Anyone else
 * sees only the app's own requests, and its row counts those. Hosts pass the answer to the sidebar
 * as its `pendingByModule`.
 */
export function teamPendingByModule(
  standings: ModuleStandings | null,
  capabilities: readonly string[] | null,
  pendingTotal: number,
): Record<string, number> {
  const counts: Record<string, number> = {};
  const wholeTeam = holdsCapability(capabilities, CAPABILITY_WRITE_MEMBER_ACCESS);
  for (const [slug, standing] of Object.entries(standings ?? {})) {
    const count = wholeTeam ? pendingTotal : (standing.pendingAccessRequests ?? 0);
    if (count > 0) counts[slug] = count;
  }
  return counts;
}

export interface OrgTeamSectionsProps {
  config: OrgConsoleConfig;
  /** The app whose own Team page this is. Absent ⇒ the Admin Console's Team access. */
  module?: string | null;
  /** Team access only: the roster narrowed to one app (`?module=`). */
  moduleFilter?: string | null;
  /** Team access only: drop that narrowing. */
  onClearModuleFilter?: () => void;
  /** The host's sign-out, for when the signed-in person removes themselves (EP-MNT9 (c)). */
  onLeft?: () => void;
}

export function OrgTeamSections({
  config,
  module,
  moduleFilter,
  onClearModuleFilter,
  onLeft,
}: OrgTeamSectionsProps) {
  const gate = useOrgAccessGate(config.orgRef);
  const prefix = `${testIdPrefix(config)}-team`;
  const slug = (module ?? '').trim();

  if (slug.length === 0) {
    return (
      <div className="contents" {...testIdProp(`${prefix}-view`)} data-team-view="organization">
        <OrgMembersPanel
          config={config}
          moduleFilter={moduleFilter ?? null}
          onClearModuleFilter={onClearModuleFilter}
          onLeft={onLeft}
        />
        <OrgInvitesPanel config={config} />
        <OrgAccessRequestsPanel config={config} />
        <OrgConnectedAiPanel config={config} />
      </div>
    );
  }

  if (gate.isPending) {
    return (
      <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-pending`)}>
        Loading…
      </p>
    );
  }

  if (holdsCapability(gate.self?.capabilities ?? null, CAPABILITY_WRITE_MEMBER_ACCESS)) {
    return (
      <div className="contents" {...testIdProp(`${prefix}-view`)} data-team-view="in-place">
        <OrgMembersPanel config={config} appColumn={slug} onLeft={onLeft} />
        <OrgInvitesPanel config={config} preselectModule={slug} appColumn={slug} />
        <OrgAccessRequestsPanel config={config} />
        <OrgConnectedAiPanel config={config} />
      </div>
    );
  }

  return (
    <div className="contents" {...testIdProp(`${prefix}-view`)} data-team-view="app">
      <OrgMembersPanel config={config} moduleFilter={slug} onLeft={onLeft} />
      <OrgInvitesPanel config={config} module={slug} />
      <OrgAccessRequestsPanel config={config} module={slug} />
    </div>
  );
}
